import React, { useCallback, useEffect, useMemo, useState, Component } from 'react';
import { Info, RefreshCw, AlertTriangle, Activity, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSessions, getReadings } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
// ─── Types ───────────────────────────────────────────────────────────
interface PiezoReading {
  timestamp: string;
  value: number;
}
interface Contraction {
  startTime: number; // ms
  endTime: number; // ms
  duration: number; // seconds
}
interface PhaseInfo {
  phase: string;
  dilationRange: string;
  rangeStart: number; // cm
  rangeEnd: number; // cm
  description: string;
  color: string; // hex used for bar + badge text
  badgeBg: string; // Tailwind class
  badgeText: string; // Tailwind class
}
interface AnalysisResult {
  contractions: Contraction[];
  averageInterval: number | null; // minutes
  averageDuration: number | null; // seconds
  contractionCount: number;
  phaseInfo: PhaseInfo;
  isInsufficient: boolean;
}
interface LaborPhaseEstimatorProps {
  token: string | null;
}
// ─── Constants ───────────────────────────────────────────────────────
const PIEZO_THRESHOLD = 2000;
const MIN_CONTRACTION_DURATION_SEC = 20;
const PHASE_PRE_LABOR: PhaseInfo = {
  phase: 'Pre-Labor / Early Signs',
  dilationRange: '0–1 cm',
  rangeStart: 0,
  rangeEnd: 1,
  description: 'Contractions are irregular and infrequent. Your body may be preparing for labor.',
  color: '#8A8A8A',
  badgeBg: 'bg-border',
  badgeText: 'text-text-secondary'
};
const PHASE_LATENT: PhaseInfo = {
  phase: 'Latent Phase (Early Labor)',
  dilationRange: '1–3 cm',
  rangeStart: 1,
  rangeEnd: 3,
  description: 'Contractions are becoming more regular. Early labor has likely begun.',
  color: '#3B82F6',
  badgeBg: 'bg-blue-500/10',
  badgeText: 'text-blue-600'
};
const PHASE_ACTIVE_EARLY: PhaseInfo = {
  phase: 'Active Labor (Early Active)',
  dilationRange: '3–5 cm',
  rangeStart: 3,
  rangeEnd: 5,
  description: 'Contractions are stronger and more frequent. Active labor is progressing.',
  color: '#6B21E8',
  badgeBg: 'bg-primary/10',
  badgeText: 'text-primary'
};
const PHASE_ACTIVE_LATE: PhaseInfo = {
  phase: 'Active Labor (Late Active)',
  dilationRange: '5–8 cm',
  rangeStart: 5,
  rangeEnd: 8,
  description: 'Labor is well established. Contractions are intense and close together.',
  color: '#00D4D4',
  badgeBg: 'bg-accent/15',
  badgeText: 'text-accent'
};
const PHASE_TRANSITION: PhaseInfo = {
  phase: 'Transition Phase',
  dilationRange: '8–10 cm',
  rangeStart: 8,
  rangeEnd: 10,
  description: 'You are in the final and most intense phase of labor. Birth is approaching.',
  color: '#F59E0B',
  badgeBg: 'bg-amber-500/15',
  badgeText: 'text-amber-600'
};
// ─── Analysis ────────────────────────────────────────────────────────
function detectContractions(readings: PiezoReading[]): Contraction[] {
  if (!readings.length) return [];
  const sorted = [...readings].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const contractions: Contraction[] = [];
  let activeStart: number | null = null;
  for (let i = 0; i < sorted.length; i++) {
    const t = new Date(sorted[i].timestamp).getTime();
    const v = sorted[i].value;
    const above = v > PIEZO_THRESHOLD;
    if (above && activeStart === null) {
      activeStart = t;
    } else if (!above && activeStart !== null) {
      const duration = (t - activeStart) / 1000;
      if (duration >= MIN_CONTRACTION_DURATION_SEC) {
        contractions.push({
          startTime: activeStart,
          endTime: t,
          duration
        });
      }
      activeStart = null;
    }
  }
  // Trailing contraction still active at end of data — only count if long enough
  if (activeStart !== null && sorted.length > 0) {
    const lastT = new Date(sorted[sorted.length - 1].timestamp).getTime();
    const duration = (lastT - activeStart) / 1000;
    if (duration >= MIN_CONTRACTION_DURATION_SEC) {
      contractions.push({
        startTime: activeStart,
        endTime: lastT,
        duration
      });
    }
  }
  return contractions;
}
function classifyPhase(averageInterval: number | null,
// mins
averageDuration: number | null,
// secs
contractionCount: number): PhaseInfo {
  if (contractionCount < 3 || averageInterval === null || averageDuration === null || averageInterval > 20) {
    return PHASE_PRE_LABOR;
  }
  if (averageInterval >= 10 && averageInterval <= 20 && averageDuration >= 20 && averageDuration <= 35) {
    return PHASE_LATENT;
  }
  if (averageInterval >= 5 && averageInterval < 10 && averageDuration >= 35 && averageDuration <= 50) {
    return PHASE_ACTIVE_EARLY;
  }
  if (averageInterval >= 3 && averageInterval < 5 && averageDuration > 50 && averageDuration <= 70) {
    return PHASE_ACTIVE_LATE;
  }
  if (averageInterval < 3 && averageDuration > 70) {
    return PHASE_TRANSITION;
  }
  // Doesn't cleanly fit any phase — fall back to closest match by interval
  if (averageInterval >= 10) return PHASE_LATENT;
  if (averageInterval >= 5) return PHASE_ACTIVE_EARLY;
  if (averageInterval >= 3) return PHASE_ACTIVE_LATE;
  return PHASE_TRANSITION;
}
function analyzeReadings(readings: PiezoReading[]): AnalysisResult {
  const contractions = detectContractions(readings);
  const contractionCount = contractions.length;
  let averageDuration: number | null = null;
  let averageInterval: number | null = null;
  if (contractionCount > 0) {
    averageDuration = contractions.reduce((s, c) => s + c.duration, 0) / contractionCount;
  }
  if (contractionCount >= 2) {
    // "gap between end of one and start of next" — end-to-start interval in minutes
    let totalGap = 0;
    for (let i = 1; i < contractions.length; i++) {
      totalGap += contractions[i].startTime - contractions[i - 1].endTime;
    }
    averageInterval = totalGap / (contractionCount - 1) / 60000;
  }
  const phaseInfo = classifyPhase(averageInterval, averageDuration, contractionCount);
  const isInsufficient = contractionCount < 3;
  return {
    contractions,
    averageInterval,
    averageDuration,
    contractionCount,
    phaseInfo,
    isInsufficient
  };
}
// ─── Component ───────────────────────────────────────────────────────
export function LaborPhaseEstimator({
  token
}: LaborPhaseEstimatorProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [readings, setReadings] = useState<PiezoReading[]>([]);
  const [showInfo, setShowInfo] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fetchData = useCallback(async (silent = false) => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(false);
    try {
      const sessionsRes = await getSessions(token);
      if (sessionsRes && sessionsRes.length > 0) {
        const latest = sessionsRes[0];
        setSessionData(latest);
        if (latest.id) {
          const r = await getReadings(token, latest.id, {
            sensorType: 'piezo',
            limit: 500
          });
          if (r && Array.isArray(r.data)) {
            setReadings(r.data);
          } else if (Array.isArray(r)) {
            setReadings(r);
          } else {
            setReadings([]);
          }
        }
      } else {
        setSessionData(null);
        setReadings([]);
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [token]);
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);
  const analysis = useMemo<AnalysisResult | null>(() => {
    if (!sessionData) return null;
    return analyzeReadings(readings);
  }, [sessionData, readings]);
  const lastUpdated = useMemo(() => {
    const ts = sessionData?.endedAt || sessionData?.updatedAt || sessionData?.createdAt;
    if (!ts) return null;
    const date = new Date(ts);
    return date.toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  }, [sessionData]);
  // ─── Render helpers ────────────────────────────────────────────────
  const renderHeader = (showRefresh: boolean) => <div className="flex items-start justify-between mb-5" data-id="element-1550" data-mp="x8b4bd2nz2h9">
      <div className="flex items-center" data-id="element-1551" data-mp="je0jh6tsfsgh">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-1552" data-mp="jcz62vpautvl">
          <Activity size={20} className="text-primary" data-id="element-1553" data-mp="1b67zq65dt2uc" />
        </div>
        <div data-id="element-1554" data-mp="1b3v7n05wtc3k">
          <div className="flex items-center" data-id="element-1555" data-mp="1b453339q4bgn">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1556" data-mp="1fxpz4prravd7">
              Labor Progress Estimator
            </h2>
            <button onClick={() => setShowInfo(true)} className="ml-2 text-text-secondary hover:text-primary transition-colors" aria-label="How this is estimated" data-id="element-1557" data-mp="30ladmm794ye2">
              <Info size={16} data-id="element-1558" data-mp="3aer4pig07ze1" />
            </button>
          </div>
          {lastUpdated && <p className="text-[12px] text-text-secondary mt-0.5" data-id="element-1559" data-mp="35g64vk4nzhq4">
              Last updated: {lastUpdated}
            </p>}
        </div>
      </div>
      {showRefresh && <button onClick={() => fetchData(true)} disabled={isRefreshing} className="w-9 h-9 rounded-full bg-secondary/70 text-primary flex items-center justify-center hover:bg-primary/10 transition-colors disabled:opacity-50" aria-label="Refresh estimate" data-id="element-1560" data-mp="2cr8yofdq9bdq">
          <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} data-id="element-1561" data-mp="6yaiuaiscza4" />
        </button>}
    </div>;
  const renderRangeBar = (info: PhaseInfo) => {
    const startPct = info.rangeStart / 10 * 100;
    const endPct = info.rangeEnd / 10 * 100;
    const widthPct = endPct - startPct;
    return <div className="mb-5" data-id="element-1562" data-mp="1h4soosc9gp4b">
        <div className="flex items-baseline justify-between mb-3" data-id="element-1563" data-mp="1h40sbfauancz">
          <span className="text-[12px] font-semibold text-text-secondary uppercase tracking-wider" data-id="element-1564" data-mp="1c5pi33gmuau8">
            Estimated Dilation
          </span>
          <span className="text-[20px] font-bold leading-none" style={{
          color: info.color
        }} data-id="element-1565" data-mp="177eaz0hq2efp">
            {info.dilationRange}
          </span>
        </div>
        <div className="relative w-full h-3 bg-secondary rounded-full overflow-hidden mb-2" data-id="element-1566" data-mp="y66pjj0b1831">
          <motion.div initial={{
          width: 0,
          left: `${startPct}%`
        }} animate={{
          width: `${widthPct}%`,
          left: `${startPct}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="absolute top-0 bottom-0 rounded-full" style={{
          backgroundColor: info.color
        }} data-id="element-1567" data-mp="t8eo9ydrs9m6" />
        </div>
        <div className="flex justify-between text-[11px] text-text-secondary font-medium px-0.5" data-id="element-1568" data-mp="34up4umi7x326">
          <span data-id="element-1569" data-mp="39sqq9e349egt">0 cm</span>
          <span data-id="element-1570" data-mp="39v4fh6b47wgg">10 cm (delivery)</span>
        </div>
      </div>;
  };
  const renderSummaryStats = (a: AnalysisResult) => <div className="grid grid-cols-3 gap-2 mb-5" data-id="element-1571" data-mp="2h42x6wc4lksp">
      <div className="bg-secondary/50 rounded-[12px] p-3 text-center" data-id="element-1572" data-mp="2h4uuqcpa9slm">
        <div className="text-[16px] font-bold text-text-primary leading-none mb-1" data-id="element-1573" data-mp="2m2nxevrdo4h5">
          {a.averageInterval !== null ? `${a.averageInterval.toFixed(1)}m` : '—'}
        </div>
        <div className="text-[11px] text-text-secondary leading-tight" data-id="element-1574" data-mp="1cotcxdgefjt">
          Avg interval
        </div>
      </div>
      <div className="bg-secondary/50 rounded-[12px] p-3 text-center" data-id="element-1575" data-mp="6aqc1h6ywcur">
        <div className="text-[16px] font-bold text-text-primary leading-none mb-1" data-id="element-1576" data-mp="22en08azeuwwn">
          {a.averageDuration !== null ? `${Math.round(a.averageDuration)}s` : '—'}
        </div>
        <div className="text-[11px] text-text-secondary leading-tight" data-id="element-1577" data-mp="2bf2zt9d19jw8">
          Avg duration
        </div>
      </div>
      <div className="bg-secondary/50 rounded-[12px] p-3 text-center" data-id="element-1578" data-mp="27fkewdl7p8te">
        <div className="text-[16px] font-bold text-text-primary leading-none mb-1" data-id="element-1579" data-mp="1jq9l5g2a4eor">
          {a.contractionCount}
        </div>
        <div className="text-[11px] text-text-secondary leading-tight" data-id="element-1580" data-mp="1erybb5bzi46j">
          Contractions
        </div>
      </div>
    </div>;
  const disclaimer = <p className="text-[11px] italic text-text-secondary leading-snug mt-1" data-id="element-1581" data-mp="1nqjr4sq2n53m">
      ⚠️ This estimate is based on sensor patterns only and should not replace a
      clinical cervical examination. Contact your doctor or midwife for an
      accurate assessment.
    </p>;
  // ─── States ────────────────────────────────────────────────────────
  if (isLoading) {
    return <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1582" data-mp="qm5i88aqazkj">
        {renderHeader(false)}
        <SkeletonBlock height="24px" width="40%" className="mb-4" data-id="element-1583" data-mp="byanynh2on5c" />
        <SkeletonBlock height="40px" borderRadius="12px" className="mb-3" data-id="element-1584" data-mp="gwly6zba4zo3" />
        <SkeletonBlock height="12px" borderRadius="999px" className="mb-5" data-id="element-1585" data-mp="gwvpcq829cvd" />
        <SkeletonBlock height="60px" borderRadius="12px" className="mb-4" data-id="element-1586" data-mp="14nhw8e6bwr3s" />
        <SkeletonBlock height="14px" width="90%" className="mb-1" data-id="element-1587" data-mp="19ljfp07o2sfs" />
        <SkeletonBlock height="14px" width="70%" data-id="element-1588" data-mp="uo0ug7u76xd3" />
      </div>;
  }
  if (error) {
    return <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1589" data-mp="zn3oyycjxl6t">
        {renderHeader(false)}
        <div className="bg-danger/5 border border-danger/15 rounded-[16px] p-5 text-center" data-id="element-1590" data-mp="3fdq8iin2uf4q">
          <AlertTriangle size={28} className="text-danger mx-auto mb-3" data-id="element-1591" data-mp="3kd2w55lrvq8d" />
          <p className="text-[14px] text-text-primary font-medium mb-1" data-id="element-1592" data-mp="3kayg5paln0rq">
            Unable to load contraction data
          </p>
          <p className="text-[13px] text-text-secondary mb-4" data-id="element-1593" data-mp="3vt7dwugbs4cg">
            Please check your connection and try again.
          </p>
          <button onClick={() => fetchData(false)} className="px-4 py-2 bg-primary text-white text-[13px] font-medium rounded-pill hover:bg-primary/90 transition-colors" data-id="element-1594" data-mp="3qvfllzbrqg7q">
            Try Again
          </button>
        </div>
        {disclaimer}
        {renderInfoSheet()}
      </div>;
  }
  if (!sessionData) {
    return <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1595" data-mp="df88qzp77j8h">
        {renderHeader(true)}
        <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-[16px] p-6 flex flex-col items-center text-center mb-4" data-id="element-1596" data-mp="8fmznwgbdui1">
          <div className="relative mb-4" data-id="element-1597" data-mp="2y7a0hbgsyp7d">
            <motion.div animate={{
            scale: [1, 1.15, 1],
            opacity: [0.5, 0.2, 0.5]
          }} transition={{
            duration: 2.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute inset-0 rounded-full bg-primary/30 blur-md" data-id="element-1598" data-mp="2t6e1ncbb5t8t" />
            <div className="relative w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center" data-id="element-1599" data-mp="1ndr8hosjnffj">
              <Activity size={26} className="text-primary" data-id="element-1600" data-mp="1sd434vpnpqso" />
            </div>
          </div>
          <p className="text-[15px] font-medium text-text-primary mb-1" data-id="element-1601" data-mp="1we5vcsl5p2l5">
            No contraction data yet
          </p>
          <p className="text-[13px] text-text-secondary max-w-[260px]" data-id="element-1602" data-mp="21d92qjolzkwa">
            Start a monitoring session to see your labor progress estimate.
          </p>
        </div>
        {disclaimer}
        {renderInfoSheet()}
      </div>;
  }
  // We have a session — render full analysis (even insufficient data falls into pre-labor)
  const a = analysis!;
  const info = a.phaseInfo;
  return <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1603" data-mp="3x4ogvyumj7n">
      {renderHeader(true)}

      {/* Phase Badge */}
      <div className="mb-5" data-id="element-1604" data-mp="3w3ia6plpqfhu">
        <span className={`inline-flex items-center px-3 py-1.5 rounded-pill text-[13px] font-semibold ${info.badgeBg} ${info.badgeText}`} data-id="element-1605" data-mp="dw2n4rpznt4d">
          {info.phase}
        </span>
      </div>

      {renderRangeBar(info)}

      {renderSummaryStats(a)}

      {/* Description */}
      <p className="text-[14px] text-text-secondary leading-relaxed mb-4" data-id="element-1606" data-mp="iv5fpcos86vg">
        {info.description}
      </p>

      {/* Insufficient data note */}
      {a.isInsufficient && <div className="bg-secondary/60 rounded-[12px] p-3 mb-4 text-[13px] text-text-primary" data-id="element-1607" data-mp="woocct6e4lfz">
          Not enough contraction data to estimate accurately. Keep monitoring.
        </div>}

      {disclaimer}

      {renderInfoSheet()}
    </div>;
  function renderInfoSheet() {
    return <AnimatePresence data-id="element-1608" data-mp="11jk7i26xboxb">
        {showInfo && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowInfo(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1609" data-mp="1l8rqqq6x3fck" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[calc(24px+env(safe-area-inset-bottom))] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1610" data-mp="11jhclgmtl2gk">
              <div className="flex justify-between items-center mb-4" data-id="element-1611" data-mp="131poot77fdtx">
                <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1612" data-mp="130wq4fiefumv">
                  About this estimate
                </h2>
                <button onClick={() => setShowInfo(false)} className="text-text-secondary hover:text-text-primary" aria-label="Close" data-id="element-1613" data-mp="u0ric49amu86">
                  <X size={22} data-id="element-1614" data-mp="k2lkpyuisa94" />
                </button>
              </div>
              <p className="text-[14px] text-text-secondary leading-relaxed" data-id="element-1615" data-mp="k4ojp8dnhdpz">
                This is an estimate based on contraction patterns detected by
                your Aurora Care belt. It is not a medical diagnosis. Always
                confirm dilation with your healthcare provider.
              </p>
            </motion.div>
          </>}
      </AnimatePresence>;
  }
}